import type { ReactNode } from "react";

import { classes } from "./classes.js";
import { VOILE_MODALE } from "./tokens.js";

/**
 * `P0-UIK-001`. 4.6.1 gives the console *"a right side panel for the
 * conversation"*; this is that panel, and the dealer space reuses it.
 *
 * Closed means absent, for the reason `Modal` gives.
 *
 * ## `round F2`, `F2.3`: it is an OVERLAY, because that is what it is drawn as
 *
 * The first cut rendered inline, so opening it pushed the list out of the way
 * and the panel became the screen. The drawing does something else
 * (`hakili-dealer.jsx`, « Ajouter un véhicule »): the page stays, dimmed, and
 * the panel comes in over its right-hand edge with a close cross on it.
 *
 * The difference is not decoration. A panel that REPLACES the list makes a
 * person lose their place in a parc of forty cars; a panel that covers part of
 * it leaves the list where it was and says, with the dimming, that the page
 * underneath is waiting rather than gone. And a close cross is the gesture
 * everyone tries first.
 *
 * `DECISION ADDED 05-36` is not touched by this: that ruling says an overlay is
 * not a STAGE. This is a panel, and a panel is allowed to be an overlay.
 */
export interface SidePanelProps {
  readonly open: boolean;
  readonly title: ReactNode;
  /** The drawing's line under the title, e.g. « Mise en ligne gratuite et immédiate ». */
  readonly subtitle?: ReactNode;
  readonly children: ReactNode;
  readonly className?: string;
  /**
   * The close cross, and the dimmed page behind it.
   *
   * Optional, and its absence is meaningful: a panel with no way out drawn on
   * it is one a person must guess at, so a caller that omits this is saying the
   * panel closes some other way and is taking responsibility for saying how.
   */
  readonly onClose?: () => void;
  /** What the cross is called, for a reader who cannot see it. */
  readonly closeLabel?: string;
}

export function SidePanel({
  open,
  title,
  subtitle,
  children,
  className,
  onClose,
  closeLabel,
}: SidePanelProps): React.JSX.Element | null {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex justify-end" data-testid="volet">
      {/* The page, dimmed and still there. Clicking it closes, which is what
        every person tries after the cross.

        `round F6b`: this wore `bg-gray-900/20`, HALF the drawn opacity. The
        drawing's veil is `rgba(17, 24, 39, 0.4)` in all 22 places it appears,
        and it is now named once as {@link VOILE_MODALE} and worn by both
        dialogs. A veil slightly too light still reads as a veil, which is why
        twenty rounds went by without anyone seeing it. */}
      <div
        className={classes("absolute inset-0", VOILE_MODALE)}
        onClick={onClose}
        aria-hidden="true"
        data-testid="volet-fond"
      />
      <aside
        className={classes(
          "relative flex h-full w-full max-w-md flex-col overflow-y-auto border-l border-gray-200 bg-white p-6 shadow-xl",
          className,
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">{title}</h2>
            {subtitle === undefined ? null : <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
          </div>
          {onClose === undefined ? null : (
            <button
              type="button"
              onClick={onClose}
              aria-label={closeLabel}
              className="-mt-1 rounded-lg p-1 text-gray-400 hover:text-gray-600"
              data-testid="volet-fermer"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                className="h-4 w-4"
                aria-hidden="true"
              >
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          )}
        </div>
        <div className="mt-4 text-sm text-gray-500">{children}</div>
      </aside>
    </div>
  );
}
